import React from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Users, Compass, RefreshCw, CheckCircle, Target } from 'lucide-react';

const ValueCircle = () => {
  const services = [
    {
      icon: <Compass size={40} />,
      title: 'Next-Generation Strategy & Governance',
      subtitle: 'Foresight and data-informed strategy that turns risk and uncertainty into your strategic advantage.',
      offerings: [
        'Strategic foresight — scenarios & horizon scanning',
        'Strategy development & execution',
        'Governance advisory & portfolio management',
        'Data insights & thought leadership',
        'Climate mitigation & adaptation strategies'
      ],
      color: 'solar-kinetic'
    },
    {
      icon: <Users size={40} />,
      title: 'Organisational Transformation & Change Management',
      subtitle: 'Building collaborative, adaptable organisations equipped to sustain impact and respond to change.',
      offerings: [
        'Organisational maturity assessment & uplift including accredited P3M3® assessments',
        'Change management & transition planning',
        'Gap analysis, blueprints, capability roadmaps',
        'Sustainability capability building & training',
        'Culture and leadership alignment, including Indigenous cultural capability building'
      ],
      color: 'solar-kinetic'
    },
    {
      icon: <Briefcase size={40} />,
      title: 'Project & Program Delivery',
      subtitle: 'Purpose-driven delivery that balances outcomes, risk and long-term sustainability.',
      offerings: [
        'Project & program management & assurance',
        'Social Impact Assessments (SIA) & social value',
        'Benefit realisation, KPI monitoring & evaluation',
        'Community & stakeholder engagement',
        'Indigenous participation pathways & Community Benefit Agreements (CBA)',
        'C&I renewable energy delivery with partners — feasibility, PPA, microgrids'
      ],
      color: 'solar-kinetic'
    }
  ];

  const impactFlow = [
    {
      icon: <Compass size={28} />,
      title: 'Strategy & Governance',
      description: 'Stewards the intelligence and foresight that informs better decisions.'
    },
    {
      icon: <RefreshCw size={28} />,
      title: 'Transformation',
      description: 'Builds the organisational capability to act on those decisions.'
    },
    {
      icon: <CheckCircle size={28} />,
      title: 'Project & Program',
      description: 'Delivers change, with people and place at the heart.'
    }
  ];

  return (
    <section className="bg-basalt-carbon text-ozone-white py-20" data-testid="value-circle-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block bg-solar-kinetic/20 text-solar-kinetic px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            Our Services
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Three Integrated <span className="text-solar-kinetic">Service Lines</span>
          </h2>
          <p className="text-xl text-ozone-white/80 max-w-3xl mx-auto">
            Designed to deliver the change our customers (and the world) need.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <div 
              key={index}
              data-testid={`service-card-${index}`}
              className="bg-ozone-white/5 backdrop-blur-sm p-8 rounded-2xl border border-ozone-white/10 hover:border-solar-kinetic/50 transition-all hover:transform hover:scale-[1.02] flex flex-col"
            >
              <div className={`text-${service.color} mb-4`}>
                {service.icon}
              </div>
              <h3 className="text-2xl font-bold mb-4 leading-tight min-h-[100px]">{service.title}</h3>
              <p className="text-solar-kinetic font-semibold mb-6 text-base min-h-[140px] leading-relaxed">{service.subtitle}</p>
              
              <div className="space-y-2">
                <p className="font-semibold text-base uppercase tracking-wide text-solar-kinetic mb-3">Key Services:</p>
                <ul className="space-y-2.5 text-base text-ozone-white/80">
                  {service.offerings.map((offering, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-solar-kinetic mr-2 flex-shrink-0">•</span>
                      <span>{offering}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* How Our Services Create Impact */}
        <div className="bg-ozone-white/5 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-solar-kinetic/20 mb-12" data-testid="impact-flow-section">
          <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center">
            How Our Services <span className="text-solar-kinetic">Create Impact</span>
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {impactFlow.map((step, index) => (
              <div key={index} className="text-center" data-testid={`impact-flow-${index}`}>
                <div className="bg-solar-kinetic text-basalt-carbon w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  {step.icon}
                </div>
                <h4 className="font-bold text-solar-kinetic mb-3 text-xl">{step.title}</h4>
                <p className="text-base text-ozone-white/85 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-ozone-white/95 mt-10 italic text-lg">
            Together, they move clients and their operating systems toward <span className="text-solar-kinetic font-semibold">inclusive and thriving futures for people and planet.</span>
          </p>
        </div>

        <div className="text-center mb-16">
          <Link
            to="/collaborate"
            data-testid="services-cta"
            className="inline-block bg-solar-kinetic text-basalt-carbon px-8 py-4 rounded-full font-semibold hover:bg-solar-kinetic/90 transition-all text-lg"
          >
            Start Your Impact Journey
          </Link>
        </div>

        {/* Key Outcomes - Three "We turn" quotes */}
        <div className="grid md:grid-cols-3 gap-8 text-center" data-testid="key-outcomes">
          <div data-testid="outcome-data" className="p-6 hover:transform hover:scale-105 transition-all">
            <div className="text-solar-kinetic text-lg mb-3">We turn uncertainty into</div>
            <div className="text-2xl font-bold text-ozone-white">confident decisions.</div>
          </div>
          <div data-testid="outcome-climate" className="p-6 hover:transform hover:scale-105 transition-all">
            <div className="text-solar-kinetic text-lg mb-3">We turn Insights into</div>
            <div className="text-2xl font-bold text-ozone-white">future-proofed resilience.</div>
          </div>
          <div data-testid="outcome-energy" className="p-6 hover:transform hover:scale-105 transition-all">
            <div className="text-solar-kinetic text-lg mb-3">We turn strategies into</div>
            <div className="text-2xl font-bold text-ozone-white">delivered impact.</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueCircle;
